import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react';
import {
  createSalt,
  derivePasswordHash,
  loadAppState,
  resetWorkspaceData,
  saveAppState,
  type AnalyticsReport,
  type AppMessage,
  type AppNotification,
  type AppProject,
  type AppSettings,
  type AppState,
  type AppUser,
  type DashboardAnnotation,
  type LicenseRequest,
  type TeamInvite,
  type Workspace,
} from './store';

type MessageInput = {
  recipient: string;
  subject: string;
  body: string;
  replyToId?: string;
};

type ReportInput = Omit<AnalyticsReport, 'id' | 'createdAt'>;
type LicenseRequestInput = Omit<LicenseRequest, 'id' | 'createdAt'>;

type AppContextValue = {
  user: AppUser | null;
  accountEmail: string;
  hasAccount: boolean;
  projects: AppProject[];
  workspaces: Workspace[];
  activeWorkspace: Workspace;
  settings: AppSettings;
  messages: AppMessage[];
  invites: TeamInvite[];
  reports: AnalyticsReport[];
  annotations: DashboardAnnotation[];
  notifications: AppNotification[];
  licenseRequests: LicenseRequest[];
  register: (name: string, email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  addProject: (project: Omit<AppProject, 'id' | 'deploys' | 'updated'>) => void;
  deleteProject: (id: string) => void;
  createWorkspace: (name: string, region: string) => Workspace;
  switchWorkspace: (id: string) => void;
  updateSettings: (patch: Partial<AppSettings>) => void;
  markMessageRead: (id: string) => void;
  toggleMessageStar: (id: string) => void;
  archiveMessage: (id: string) => void;
  restoreMessage: (id: string) => void;
  sendMessage: (input: MessageInput) => void;
  saveDraft: (input: Omit<MessageInput, 'replyToId'>) => void;
  inviteMember: (email: string, role: AppUser['role']) => TeamInvite;
  revokeInvite: (id: string) => void;
  createReport: (input: ReportInput) => AnalyticsReport;
  addAnnotation: (text: string) => DashboardAnnotation;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  addLicenseRequest: (input: LicenseRequestInput) => LicenseRequest;
  resetWorkspace: () => void;
};

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState(loadAppState);

  function commit(mutator: (current: AppState) => AppState): void {
    setState((current) => {
      const next = mutator(current);
      saveAppState(next);
      return next;
    });
  }

  function makeNotification(
    title: string,
    message: string,
    tone: AppNotification['tone'] = 'info',
  ): AppNotification {
    return {
      id: crypto.randomUUID(),
      title,
      message,
      tone,
      createdAt: new Date().toISOString(),
      read: false,
    };
  }

  const value = useMemo<AppContextValue>(() => {
    const activeWorkspace =
      state.workspaces.find((workspace) => workspace.id === state.activeWorkspaceId) ??
      state.workspaces[0];

    if (!activeWorkspace) {
      throw new Error('Application state requires at least one workspace.');
    }

    return {
      user: state.user,
      accountEmail: state.account?.email ?? '',
      hasAccount: Boolean(state.account),
      projects: state.projects,
      workspaces: state.workspaces,
      activeWorkspace,
      settings: state.settings,
      messages: state.messages,
      invites: state.invites,
      reports: state.reports,
      annotations: state.annotations,
      notifications: state.notifications,
      licenseRequests: state.licenseRequests,

      async register(name, email, password) {
        const normalizedEmail = email.trim().toLowerCase();
        const salt = createSalt();
        const passwordHash = await derivePasswordHash(password, salt);
        const account = {
          id: crypto.randomUUID(),
          name: name.trim(),
          email: normalizedEmail,
          role: 'Admin' as const,
          salt,
          passwordHash,
        };
        commit((current) => ({
          ...current,
          account,
          user: { id: account.id, name: account.name, email: account.email, role: account.role },
        }));
      },

      async login(email, password) {
        const account = state.account;
        if (!account) return false;
        const normalizedEmail = email.trim().toLowerCase();
        if (normalizedEmail !== account.email) return false;
        const passwordHash = await derivePasswordHash(password, account.salt);
        if (passwordHash !== account.passwordHash) return false;
        commit((current) => ({
          ...current,
          user: {
            id: account.id,
            name: account.name,
            email: account.email,
            role: account.role,
          },
        }));
        return true;
      },

      logout() {
        commit((current) => ({ ...current, user: null }));
      },

      addProject(project) {
        commit((current) => ({
          ...current,
          projects: [
            { ...project, id: crypto.randomUUID(), deploys: 0, updated: 'just now' },
            ...current.projects,
          ],
          notifications: [
            makeNotification('Project created', project.name + ' was added to the portfolio.', 'success'),
            ...current.notifications,
          ],
        }));
      },

      deleteProject(id) {
        commit((current) => {
          const removed = current.projects.find((project) => project.id === id);
          return {
            ...current,
            projects: current.projects.filter((project) => project.id !== id),
            notifications: removed
              ? [
                  makeNotification('Project removed', removed.name + ' was removed from the local portfolio.', 'warning'),
                  ...current.notifications,
                ]
              : current.notifications,
          };
        });
      },

      createWorkspace(name, region) {
        const workspace = { id: crypto.randomUUID(), name: name.trim(), region };
        commit((current) => ({
          ...current,
          workspaces: [...current.workspaces, workspace],
          activeWorkspaceId: workspace.id,
          settings: { ...current.settings, workspaceName: workspace.name },
          notifications: [
            makeNotification('Workspace created', workspace.name + ' is now active.', 'success'),
            ...current.notifications,
          ],
        }));
        return workspace;
      },

      switchWorkspace(id) {
        commit((current) => {
          const workspace = current.workspaces.find((item) => item.id === id);
          if (!workspace) return current;
          return {
            ...current,
            activeWorkspaceId: workspace.id,
            settings: { ...current.settings, workspaceName: workspace.name },
          };
        });
      },

      updateSettings(patch) {
        commit((current) => {
          const settings = { ...current.settings, ...patch };
          const workspaces = current.workspaces.map((workspace) =>
            workspace.id === current.activeWorkspaceId
              ? { ...workspace, name: settings.workspaceName }
              : workspace,
          );
          return { ...current, settings, workspaces };
        });
      },

      markMessageRead(id) {
        commit((current) => ({
          ...current,
          messages: current.messages.map((message) =>
            message.id === id ? { ...message, unread: false } : message,
          ),
        }));
      },

      toggleMessageStar(id) {
        commit((current) => ({
          ...current,
          messages: current.messages.map((message) =>
            message.id === id ? { ...message, starred: !message.starred } : message,
          ),
        }));
      },

      archiveMessage(id) {
        commit((current) => ({
          ...current,
          messages: current.messages.map((message) =>
            message.id === id ? { ...message, folder: 'archive', unread: false } : message,
          ),
        }));
      },

      restoreMessage(id) {
        commit((current) => ({
          ...current,
          messages: current.messages.map((message) =>
            message.id === id ? { ...message, folder: 'inbox' } : message,
          ),
        }));
      },

      sendMessage(input) {
        commit((current) => {
          const parent = input.replyToId
            ? current.messages.find((message) => message.id === input.replyToId)
            : undefined;
          const sender = current.user?.name ?? 'Workspace Admin';
          const message: AppMessage = {
            id: crypto.randomUUID(),
            threadId: parent?.threadId ?? crypto.randomUUID(),
            sender,
            recipient: input.recipient.trim(),
            subject: input.subject.trim(),
            body: input.body.trim(),
            createdAt: new Date().toISOString(),
            folder: 'sent',
            label: parent?.label ?? 'General',
            unread: false,
            starred: false,
          };
          return { ...current, messages: [message, ...current.messages] };
        });
      },

      saveDraft(input) {
        commit((current) => {
          const sender = current.user?.name ?? 'Workspace Admin';
          const message: AppMessage = {
            id: crypto.randomUUID(),
            threadId: crypto.randomUUID(),
            sender,
            recipient: input.recipient.trim(),
            subject: input.subject.trim() || '(no subject)',
            body: input.body.trim(),
            createdAt: new Date().toISOString(),
            folder: 'drafts',
            label: 'Draft',
            unread: false,
            starred: false,
          };
          return { ...current, messages: [message, ...current.messages] };
        });
      },

      inviteMember(email, role) {
        const invite: TeamInvite = {
          id: crypto.randomUUID(),
          email: email.trim().toLowerCase(),
          role,
          createdAt: new Date().toISOString(),
          status: 'pending',
        };
        commit((current) => ({
          ...current,
          invites: [invite, ...current.invites],
          notifications: [
            makeNotification('Invite created', invite.email + ' was added to the pending invite queue.', 'success'),
            ...current.notifications,
          ],
        }));
        return invite;
      },

      revokeInvite(id) {
        commit((current) => ({
          ...current,
          invites: current.invites.filter((invite) => invite.id !== id),
        }));
      },

      createReport(input) {
        const report: AnalyticsReport = {
          id: crypto.randomUUID(),
          ...input,
          createdAt: new Date().toISOString(),
        };
        commit((current) => ({
          ...current,
          reports: [report, ...current.reports],
          notifications: [
            makeNotification('Report generated', report.name + ' is ready.', 'success'),
            ...current.notifications,
          ],
        }));
        return report;
      },

      addAnnotation(text) {
        const annotation: DashboardAnnotation = {
          id: crypto.randomUUID(),
          text: text.trim(),
          createdAt: new Date().toISOString(),
        };
        commit((current) => ({
          ...current,
          annotations: [annotation, ...current.annotations],
        }));
        return annotation;
      },

      markNotificationRead(id) {
        commit((current) => ({
          ...current,
          notifications: current.notifications.map((notification) =>
            notification.id === id ? { ...notification, read: true } : notification,
          ),
        }));
      },

      markAllNotificationsRead() {
        commit((current) => ({
          ...current,
          notifications: current.notifications.map((notification) => ({ ...notification, read: true })),
        }));
      },

      addLicenseRequest(input) {
        const request: LicenseRequest = {
          id: crypto.randomUUID(),
          ...input,
          createdAt: new Date().toISOString(),
        };
        commit((current) => ({
          ...current,
          licenseRequests: [request, ...current.licenseRequests],
        }));
        return request;
      },

      resetWorkspace() {
        commit((current) => resetWorkspaceData(current));
      },
    };
  }, [state]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
